import React, { useEffect, useState } from "react";
import api from "@/api";

// Reusable avatar that lazy-loads the actual base64 photo from a per-record
// endpoint on mount. Bulk list responses no longer ship inline data URLs (we
// were hitting Cloudflare's 520 parse limit at conference scale), so each
// avatar fetches its own photo when it renders.
//
// Props:
//   endpoint  — relative API path that returns {photo_url}, e.g. "/users/<id>/photo"
//   hasPhoto  — boolean from the list payload; skip the fetch if false
//   name      — display name, used to render the initial letter fallback
//   className — tailwind classes for the outer wrapper (size, shape, border, etc.)
//   src       — optional pre-set src to skip the fetch entirely (admin upload preview)
//   round     — set true to render initials in a circular fallback (default true)
export default function LazyAvatar({ endpoint, hasPhoto, name = "", className = "", src = null, round = true, alt }) {
  const [loaded, setLoaded] = useState(src || "");
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (src) { setLoaded(src); return; }
    if (!hasPhoto || !endpoint) return;
    let active = true;
    api.get(endpoint)
      .then((r) => { if (active) setLoaded(r.data?.photo_url || ""); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; };
  }, [endpoint, hasPhoto, src]);

  const initial = (name || "").trim().charAt(0).toUpperCase() || "?";
  const wrapperCls = `${className} bg-[#E5E9F0] overflow-hidden${round && !/rounded/.test(className) ? " rounded-full" : ""}`;

  if (loaded && !err) {
    return (
      <div className={wrapperCls} data-has-photo="true">
        <img src={loaded} alt={alt || name} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={`${wrapperCls} grid place-items-center text-[#7A828C] text-sm`} data-has-photo="false">
      <span aria-hidden="true">{initial}</span>
    </div>
  );
}
